/**
 * NOLAS 2026 : © Mika (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/i18n/fr.twoFactor.ts
 *
 * Rôle :
 *   Sous-catalogue i18n du lot « double authentification au choix : e-mail OU
 *   application ». Fusionné dans `messagesFr` par `fr.ts`.
 *
 *   Pourquoi un fichier séparé : `fr.ts` fait ~6 700 lignes et c'est LA
 *   dépendance partagée du dépôt. Depuis le lot CIRFA, chaque chantier écrit
 *   ses chaînes chez lui : les chantiers menés en parallèle ne se gênent pas
 *   sur leurs fichiers, ils se gênent sur les fichiers communs.
 *
 * Règles de wording appliquées ici (elles ne sont pas décoratives) :
 *   1. ZÉRO JARGON. On ne dit jamais « TOTP », « RFC 6238 », « OTP » ni
 *      « Base32 » à l'utilisateur : on dit « application d'authentification »
 *      et « clé ». Le lot précédent a montré que le TOTP est resté inutilisé
 *      pendant trois mois faute d'accompagnement : pas faute de technique.
 *   2. AUCUNE IDENTITÉ EN DUR (nom d'événement, lieu, organisateur) : le nom
 *      affiché dans l'application vient du serveur (`issuer`), interpolé.
 *      Un test de non-régression l'impose (`no-hardcoded-identity.test.ts`).
 *   3. APOSTROPHE TYPOGRAPHIQUE « ’ » PARTOUT. En syntaxe ICU, l'apostrophe
 *      DROITE échappe les accolades : « d'{issuer} » ferait DISPARAÎTRE le
 *      token du rendu. Deux tests interdisent ce motif.
 *   4. Messages d'erreur pédagogiques, jamais accusatoires, et qui ne révèlent
 *      jamais si un compte existe.
 *
 * @author Mika
 * @since  2026-08-03 (lot « 2FA au choix e-mail ou application »)
 */

export const messagesTwoFactorFr: Record<string, string> = {
  // ═══════════════════════════════════════════════════════════════════════
  // Parcours guidé : cadre commun
  // ═══════════════════════════════════════════════════════════════════════
  'twoFactor.wizard.title': 'Sécuriser votre connexion',
  'twoFactor.wizard.subtitle':
    'À chaque connexion, un code à usage unique vous sera demandé en plus de votre mot de passe. Choisissez comment vous préférez le recevoir.',
  'twoFactor.wizard.stepper.aria': 'Progression de la configuration',
  'twoFactor.wizard.stepOf': 'Étape {current} sur {total}',
  'twoFactor.wizard.step.method': 'Votre choix',
  'twoFactor.wizard.step.install': 'Installation',
  'twoFactor.wizard.step.link': 'Liaison',
  'twoFactor.wizard.step.verify': 'Vérification',
  'twoFactor.wizard.step.codes': 'Codes de secours',
  'twoFactor.wizard.back': 'Revenir à l’étape précédente',
  'twoFactor.wizard.loading': 'Chargement…',
  'twoFactor.wizard.quit': 'Quitter pour l’instant',

  // ═══════════════════════════════════════════════════════════════════════
  // Étape 1 : Choisir sa méthode
  // ═══════════════════════════════════════════════════════════════════════
  'twoFactor.method.title': 'Comment souhaitez-vous recevoir votre code ?',
  'twoFactor.method.intro':
    'Les deux méthodes protègent votre compte de la même façon. Choisissez celle qui vous arrange ; vous pourrez en changer quand vous voudrez.',
  'twoFactor.method.aria.group': 'Choix de la méthode de double authentification',
  'twoFactor.method.email.title': 'Par e-mail',
  'twoFactor.method.email.benefit':
    'Vous recevez un code à 6 chiffres par e-mail. Simple, rien à installer.',
  'twoFactor.method.email.detail':
    'Il faut avoir accès à votre boîte e-mail au moment de vous connecter.',
  'twoFactor.method.email.choose': 'Choisir l’e-mail',
  'twoFactor.method.app.title': 'Par application',
  'twoFactor.method.app.benefit':
    'Un code s’affiche dans une application sur votre téléphone. Plus rapide, fonctionne même sans réseau ni e-mail.',
  'twoFactor.method.app.detail':
    'Il faut installer une application gratuite une seule fois, puis relier votre compte.',
  'twoFactor.method.app.choose': 'Choisir l’application',
  'twoFactor.method.app.unavailable':
    'La méthode par application n’est pas proposée pour le moment. Rapprochez-vous de votre administrateur.',
  'twoFactor.method.badge.current': 'Active',

  // ═══════════════════════════════════════════════════════════════════════
  // Étape 2 : Installer une application
  // ═══════════════════════════════════════════════════════════════════════
  'twoFactor.install.title': 'Installer une application',
  'twoFactor.install.intro':
    'Une seule suffit, et elles sont toutes gratuites. Si vous en avez déjà une, gardez-la : passez directement à l’étape suivante.',
  'twoFactor.install.free': 'Gratuite',
  'twoFactor.install.appStore': 'App Store (iPhone)',
  'twoFactor.install.playStore': 'Google Play (Android)',
  'twoFactor.install.linkAria': '{app}, ouvrir {store}',
  'twoFactor.install.app.google': 'Google Authenticator',
  'twoFactor.install.app.microsoft': 'Microsoft Authenticator',
  'twoFactor.install.app.authy': 'Authy',
  'twoFactor.install.app.freeotp': 'FreeOTP',
  'twoFactor.install.app.bitwarden': 'Bitwarden',
  'twoFactor.install.app.onepassword': '1Password',
  'twoFactor.install.qr.summary': 'Installer depuis mon téléphone',
  'twoFactor.install.qr.help':
    'Utile si vous lisez cette page sur un ordinateur : visez ce carré avec l’appareil photo de votre téléphone, il ouvrira la bonne page.',
  'twoFactor.install.qr.alt': 'Carré à scanner, {app} sur {store}',
  'twoFactor.install.builtin.title': 'Ou le gestionnaire intégré à votre téléphone',
  'twoFactor.install.builtin.body':
    'Sur iPhone, « Réglages » puis « Mots de passe » sait afficher ces codes. Sur Android, le gestionnaire de mots de passe de Google le fait aussi. Rien à installer dans ce cas.',
  'twoFactor.install.next': 'J’ai une application, continuer',

  // ═══════════════════════════════════════════════════════════════════════
  // Étape 3 : Relier son compte
  // ═══════════════════════════════════════════════════════════════════════
  'twoFactor.link.title': 'Relier votre compte',
  'twoFactor.link.intro':
    'Ouvrez votre application, choisissez « Ajouter un compte », puis scannez ce carré.',
  'twoFactor.link.issuer':
    'Dans votre application, la ligne s’appellera « {issuer} ».',
  'twoFactor.link.qr.alt':
    'Carré à scanner avec votre application d’authentification',
  // Repli « pas de carré » (2026-08-03). Le serveur peut ne pas savoir
  // fabriquer l’image ; la clé recopiée à la main relie le compte exactement
  // de la même façon. On le dit sans dramatiser : rien n’est bloqué.
  'twoFactor.link.intro.manualOnly':
    'Ouvrez votre application, choisissez « Ajouter un compte », puis « Saisir une clé ».',
  'twoFactor.link.qr.unavailable':
    'Le carré à scanner n’a pas pu être fabriqué sur ce serveur. Ce n’est pas bloquant : reliez votre compte avec la clé ci-dessous, elle fait exactement la même chose.',
  'twoFactor.link.manual.title': 'La clé à saisir à la main',
  'twoFactor.link.manual.bodyNoQr':
    'Dans votre application, choisissez « Saisir une clé », puis recopiez ce qui suit. Le type de compte à choisir est « basé sur le temps » : c’est le réglage par défaut.',
  'twoFactor.link.cannotScan': 'Je ne peux pas scanner ce carré',
  'twoFactor.link.manual.body':
    'C’est le cas si vous lisez cette page sur le téléphone qui porte l’application : il ne peut pas se scanner lui-même. Dans votre application, choisissez « Saisir une clé » puis recopiez ce qui suit.',
  'twoFactor.link.manual.accountLabel': 'Nom du compte',
  'twoFactor.link.manual.keyLabel': 'Clé',
  'twoFactor.link.manual.keyHint':
    'Les espaces sont là pour la lisibilité : votre application les ignore.',
  'twoFactor.link.copy': 'Copier la clé',
  'twoFactor.link.copied': 'Clé copiée.',
  'twoFactor.link.copyFailed':
    'La copie automatique n’a pas fonctionné. Sélectionnez la clé et copiez-la à la main.',
  'twoFactor.link.next': 'C’est fait, vérifier maintenant',
  'twoFactor.link.retry': 'Préparer une nouvelle clé',
  'twoFactor.link.error':
    'La préparation n’a pas abouti. Réessayez dans un instant.',
  'twoFactor.link.expiresIn':
    'Cette clé reste valable {minutes} minutes. Passé ce délai, revenez ici : une nouvelle sera préparée, et vous n’aurez rien perdu.',
  'twoFactor.link.safety':
    'Tant que vous n’aurez pas saisi un code valide à l’étape suivante, rien n’est modifié sur votre compte : vous ne risquez pas de rester dehors.',

  // ═══════════════════════════════════════════════════════════════════════
  // Étape 4 : Vérifier (obligatoire)
  // ═══════════════════════════════════════════════════════════════════════
  'twoFactor.verify.title': 'Vérifier que tout fonctionne',
  'twoFactor.verify.intro':
    'Saisissez le code à 6 chiffres affiché en face du nom de votre compte dans l’application.',
  'twoFactor.verify.submit': 'Vérifier et activer',
  'twoFactor.verify.help.title': 'Le code est refusé ?',
  'twoFactor.verify.help.clock':
    'Vérifiez que l’heure de votre téléphone est réglée automatiquement : c’est la cause la plus fréquente.',
  'twoFactor.verify.help.rotation':
    'Le code change toutes les 30 secondes. Saisissez celui qui est affiché maintenant, sans attendre.',
  'twoFactor.verify.help.wrongLine':
    'Si vous avez plusieurs comptes dans l’application, vérifiez que vous lisez la bonne ligne.',

  // ═══════════════════════════════════════════════════════════════════════
  // Étape 5 : Codes de secours
  // ═══════════════════════════════════════════════════════════════════════
  'twoFactor.codes.title': 'Vos codes de secours',
  'twoFactor.codes.intro':
    'Ils servent à vous connecter si vous perdez votre téléphone. Chacun ne fonctionne qu’une fois.',
  'twoFactor.codes.onceOnly':
    'Ces codes ne seront plus jamais affichés. Mettez-les à l’abri maintenant, ailleurs que dans votre téléphone.',
  'twoFactor.codes.aria.list': 'Liste de vos codes de secours',
  'twoFactor.codes.download': 'Télécharger (.txt)',
  'twoFactor.codes.print': 'Imprimer',
  'twoFactor.codes.copy': 'Copier',
  'twoFactor.codes.copied': 'Codes copiés.',
  'twoFactor.codes.downloaded': 'Fichier téléchargé.',
  'twoFactor.codes.confirm': 'J’ai mis ces codes en lieu sûr.',
  'twoFactor.codes.confirmRequired':
    'Cochez la case ci-dessus pour terminer : c’est votre seule porte de sortie si vous perdez votre téléphone.',
  'twoFactor.codes.finish': 'Terminer',
  'twoFactor.codes.printTitle': 'Codes de secours, {issuer}',

  // ═══════════════════════════════════════════════════════════════════════
  // Écran de confirmation final
  // ═══════════════════════════════════════════════════════════════════════
  'twoFactor.done.app.title': 'C’est activé',
  'twoFactor.done.app.body':
    'À votre prochaine connexion, votre mot de passe puis le code affiché par votre application vous seront demandés.',
  'twoFactor.done.email.title': 'C’est enregistré',
  'twoFactor.done.email.body':
    'À votre prochaine connexion, vous recevrez un code à 6 chiffres par e-mail après votre mot de passe.',
  'twoFactor.done.manage':
    'Vous pouvez changer de méthode ou régénérer vos codes à tout moment depuis votre page « Sécurité ».',
  'twoFactor.done.lostPhone.link': 'Que faire si je perds mon téléphone ?',
  'twoFactor.done.lostPhone.title': 'Si vous perdez votre téléphone',
  'twoFactor.done.lostPhone.body1':
    'Connectez-vous avec un de vos codes de secours : à l’écran du code, choisissez « Utiliser un code de secours ».',
  'twoFactor.done.lostPhone.body2':
    'Si vous n’avez plus de code de secours, un administrateur peut remettre votre compte en réception par e-mail. Contactez-le.',
  'twoFactor.done.goSecurity': 'Voir ma page Sécurité',
  'twoFactor.done.goBack': 'Revenir au back-office',

  // ═══════════════════════════════════════════════════════════════════════
  // Champ de code + indicateur de temps restant (§3 du brief)
  // ═══════════════════════════════════════════════════════════════════════
  'twoFactor.code.legend': 'Code à 6 chiffres',
  'twoFactor.code.digitAria': 'Chiffre {position} sur {total}',
  'twoFactor.code.hint':
    'Le collage d’un code entier fonctionne. La validation se fait dès le 6ᵉ chiffre.',
  'twoFactor.countdown.label': 'Ce code change dans {seconds} s',
  'twoFactor.countdown.aria': 'Temps restant avant le changement de code',

  // ═══════════════════════════════════════════════════════════════════════
  // Connexion : saisie du code d’application / code de secours
  // ═══════════════════════════════════════════════════════════════════════
  'twoFactor.login.title': 'Votre code de connexion',
  'twoFactor.login.intro':
    'Ouvrez votre application d’authentification et recopiez le code à 6 chiffres.',
  'twoFactor.login.submit': 'Se connecter',
  'twoFactor.login.useRecovery': 'Utiliser un code de secours',
  'twoFactor.login.lostAccess': 'Je n’ai plus accès à mon application',
  'twoFactor.login.lostAccess.title': 'Vous n’avez plus votre application ?',
  'twoFactor.login.lostAccess.body1':
    'Utilisez un de vos codes de secours : c’est exactement à cela qu’ils servent.',
  'twoFactor.login.lostAccess.body2':
    'Si vous n’en avez plus, seul un administrateur peut remettre votre compte en réception par e-mail.',
  'twoFactor.login.back': 'Revenir à la page de connexion',
  // FIX 2026-08-03 (vague 2, constat S1) : le serveur réclame une deuxième
  // étape que cet écran ne sait pas servir (instance dont l'API n'est pas
  // encore déployée, méthode hors contrat). On le dit sur place : naviguer
  // « au mieux » produisait un 404 ou une boucle de redirection muette.
  'twoFactor.login.unsupportedMethod':
    'Votre compte demande une vérification que cette version de l’application ne sait pas afficher. Rapprochez-vous de votre administrateur : la mise à jour du serveur n’est probablement pas terminée.',
  'twoFactor.login.recovery.title': 'Code de secours',
  'twoFactor.login.recovery.intro':
    'Saisissez un de vos codes de secours. Il ne fonctionnera qu’une fois.',
  'twoFactor.login.recovery.label': 'Code de secours',
  'twoFactor.login.recovery.hint':
    'Cinq caractères, un tiret, cinq caractères. Les minuscules et les espaces sont acceptés.',
  'twoFactor.login.recovery.submit': 'Valider ce code',
  'twoFactor.login.recovery.useApp': 'Revenir au code de mon application',
  'twoFactor.login.recovery.remaining':
    'Il vous reste {count, plural, one {# code de secours} other {# codes de secours}}.',
  'twoFactor.login.recovery.low':
    'Attention : il ne vous reste que {count, plural, one {# code de secours} other {# codes de secours}}. Pensez à en générer de nouveaux depuis votre page « Sécurité ».',

  // ═══════════════════════════════════════════════════════════════════════
  // Messages d’erreur : pédagogiques, jamais accusatoires
  // ═══════════════════════════════════════════════════════════════════════
  'twoFactor.error.codeInvalid':
    'Ce code n’est pas valide. Vérifiez que l’heure de votre téléphone est réglée automatiquement, le code change toutes les 30 secondes.',
  'twoFactor.error.codeInvalidWithAttempts':
    'Ce code n’est pas valide. Vérifiez que l’heure de votre téléphone est réglée automatiquement. Il vous reste {attempts, plural, one {# essai} other {# essais}}.',
  'twoFactor.error.codeReplayed':
    'Ce code a déjà servi. Attendez qu’un nouveau code s’affiche dans votre application, puis recommencez.',
  'twoFactor.error.noPendingLogin':
    'Votre connexion n’est plus en cours. Reprenez depuis la page de connexion.',
  'twoFactor.error.noPendingSetup':
    'La configuration a expiré. Reprenez à l’étape de liaison, une nouvelle clé va être préparée.',
  'twoFactor.error.noRecoveryCodes':
    'Aucun code de secours n’est utilisable sur ce compte. Rapprochez-vous de votre administrateur, qui peut vous remettre en réception par e-mail.',
  'twoFactor.error.encryptionUnavailable':
    'La méthode par application ne peut pas être activée sur cette installation. Rien n’a été modifié sur votre compte.',
  'twoFactor.error.methodInvalid':
    'Ce changement n’est pas possible par ce chemin. Passez par le parcours guidé.',
  'twoFactor.error.methodNotApp':
    'Les codes de secours ne concernent que la méthode par application.',
  'twoFactor.error.rateLimited':
    'Trop de tentatives. Patientez quelques minutes avant de réessayer.',
  'twoFactor.error.network':
    'La demande n’a pas abouti. Vérifiez votre connexion, puis réessayez.',
  // Section C : administration. Ajoutés en vague 2 : ces refus étaient
  // parfaitement explicites côté serveur et retombaient sur « vérifiez votre
  // connexion », c’est-à-dire sur une fausse piste.
  'twoFactor.error.methodsEmpty':
    'Il faut proposer au moins une méthode de réception du code.',
  'twoFactor.error.emailRequired':
    'L’e-mail ne peut pas être retiré : c’est le seul moyen qui ne demande rien à installer, et donc le seul recours universel.',
  'twoFactor.error.forbidden':
    'Cette action est réservée. Votre compte n’a pas les droits nécessaires.',
  'twoFactor.error.userInvalid':
    'Ce compte n’a pas pu être identifié. Rechargez la liste, puis réessayez.',
  'twoFactor.error.userNotFound':
    'Ce compte n’existe plus. Rechargez la liste pour voir l’état réel.',
  // Trois refus serveur qui retombaient sur « vérifiez votre connexion »,
  // c’est-à-dire sur une fausse piste (ajoutés le 2026-08-03).
  'twoFactor.error.qrUnavailable':
    'Le carré à scanner n’a pas pu être fabriqué sur ce serveur. Ce n’est pas votre connexion. Recommencez : la clé en toutes lettres vous sera proposée, elle relie votre compte tout aussi bien.',
  'twoFactor.error.methodNotAllowed':
    'La connexion par application n’est pas proposée ici. Vous continuez à recevoir vos codes par e-mail, vous n’avez rien à faire.',
  'twoFactor.error.schemaNotReady':
    'Cette fonction n’est pas encore prête sur cet espace. Signalez-le à votre administrateur : rien n’a été modifié sur votre compte.',

  // ═══════════════════════════════════════════════════════════════════════
  // Page « Sécurité » du compte (§4 du brief)
  // ═══════════════════════════════════════════════════════════════════════
  'twoFactor.security.title': 'Sécurité de mon compte',
  'twoFactor.security.subtitle':
    'Choisissez comment vous recevez votre code de connexion et gérez vos codes de secours.',
  'twoFactor.security.loading': 'Chargement de vos réglages…',
  'twoFactor.security.loadError':
    'Impossible de lire vos réglages de sécurité. Vérifiez votre connexion, puis réessayez.',
  'twoFactor.security.retry': 'Réessayer',
  'twoFactor.security.method.title': 'Méthode active',
  'twoFactor.security.method.email': 'Code reçu par e-mail',
  'twoFactor.security.method.app': 'Code affiché par une application',
  'twoFactor.security.method.emailHelp':
    'Rien à installer : le code arrive dans votre boîte e-mail.',
  'twoFactor.security.method.appHelp':
    'Le code s’affiche sur votre téléphone, même sans réseau.',
  'twoFactor.security.updatedAt': 'Dernière modification : {date}',
  'twoFactor.security.updatedNever': 'Jamais modifiée depuis la création du compte.',
  'twoFactor.security.switchToApp': 'Passer à l’application',
  'twoFactor.security.switchToEmail': 'Revenir à l’e-mail',
  'twoFactor.security.switchToEmail.title': 'Revenir à la réception par e-mail ?',
  'twoFactor.security.switchToEmail.body':
    'Votre application sera déliée de ce compte et vos codes de secours seront invalidés. Vous recevrez de nouveau votre code par e-mail. Vous pourrez revenir à l’application quand vous voudrez.',
  'twoFactor.security.switchToEmail.confirm': 'Revenir à l’e-mail',
  'twoFactor.security.switchedToEmail':
    'Vous recevrez désormais votre code par e-mail.',
  'twoFactor.security.appUnavailable':
    'La méthode par application n’est pas disponible sur cette installation.',
  'twoFactor.security.appNotAllowed':
    'Votre administrateur ne propose pas la méthode par application.',
  'twoFactor.security.codes.title': 'Codes de secours',
  'twoFactor.security.codes.intro':
    'Ils vous permettent de vous connecter si vous perdez votre téléphone. Chacun ne fonctionne qu’une fois.',
  'twoFactor.security.codes.remaining':
    '{remaining, plural, one {# code encore utilisable} other {# codes encore utilisables}} sur {total}.',
  'twoFactor.security.codes.none':
    'Aucun code de secours utilisable sur ce compte.',
  'twoFactor.security.codes.low':
    'Il vous en reste peu : générez-en de nouveaux pour ne pas vous retrouver bloqué.',
  'twoFactor.security.codes.regenerate': 'Générer de nouveaux codes',
  'twoFactor.security.codes.regenerate.title': 'Générer de nouveaux codes ?',
  'twoFactor.security.codes.regenerate.body':
    'Vos codes actuels seront invalidés immédiatement. Les nouveaux ne seront affichés qu’une seule fois : soyez prêt à les mettre à l’abri.',
  'twoFactor.security.codes.regenerate.confirm': 'Générer',
  'twoFactor.security.codes.regenerated':
    'De nouveaux codes de secours ont été générés.',
  'twoFactor.security.cancel': 'Annuler',
  'twoFactor.security.lostPhone.title': 'Si je perds mon téléphone',
  'twoFactor.security.lostPhone.body':
    'Connectez-vous avec un code de secours, puis reliez votre nouveau téléphone depuis cette page. Sans code de secours, seul un administrateur peut vous débloquer.',
  // FIX 2026-08-03 (vague 2, constat M4) : la case « J’ai mis ces codes en
  // lieu sûr » n’était obligatoire que dans l’assistant : après une
  // régénération depuis cette page, elle était décorative et rien n’empêchait
  // de quitter l’écran sans avoir noté les codes, qui ne sont plus jamais
  // réaffichés. Ce bouton rend la case bloquante des deux côtés.
  'twoFactor.security.codes.done': 'J’ai noté mes nouveaux codes',

  // ═══════════════════════════════════════════════════════════════════════
  // Administration de la double authentification (section C du contrat)
  // ═══════════════════════════════════════════════════════════════════════
  'twoFactor.admin.title': 'Double authentification des comptes',
  'twoFactor.admin.subtitle':
    'Choisissez les méthodes proposées aux comptes de cet événement, voyez qui utilise quoi, et débloquez une personne qui a perdu son téléphone.',
  'twoFactor.admin.loading': 'Chargement des réglages et des comptes…',
  'twoFactor.admin.loadError':
    'Impossible de lire les réglages de double authentification. Vérifiez votre connexion, puis réessayez.',
  'twoFactor.admin.retry': 'Réessayer',
  'twoFactor.admin.method.email': 'Code reçu par e-mail',
  'twoFactor.admin.method.app': 'Code affiché par une application',
  'twoFactor.admin.method.emailHelp':
    'Rien à installer. Fonctionne pour un compte créé le matin même.',
  'twoFactor.admin.method.appHelp':
    'Un code s’affiche sur le téléphone, même sans réseau ni e-mail.',
  'twoFactor.admin.method.emailLocked':
    'L’e-mail ne peut jamais être retiré : c’est le seul recours qui ne demande rien à installer. Le retirer enfermerait dehors toute personne sans téléphone compatible, y compris celle qui décoche la case.',
  'twoFactor.admin.method.appUnavailable':
    'Cette installation ne peut pas conserver les clés en sécurité : la méthode par application n’est donc pas proposable ici. On préfère ne pas la proposer plutôt que la proposer et mal la stocker.',
  'twoFactor.admin.methods.title': 'Méthodes proposées',
  'twoFactor.admin.methods.intro':
    'Chaque personne choisit ensuite librement parmi ce que vous proposez, depuis sa page « Ma sécurité ».',
  'twoFactor.admin.methods.removeAppWarning':
    'Si vous retirez la méthode par application, les comptes qui l’utilisaient repassent immédiatement à l’e-mail : personne ne se retrouve bloqué. Leur liaison est conservée, vous pouvez donc revenir en arrière sans que personne ait à tout refaire.',
  'twoFactor.admin.methods.switchedBack':
    '{count, plural, =0 {Aucun compte n’a eu besoin d’être rebasculé.} one {# compte a été rebasculé vers l’e-mail.} other {# comptes ont été rebasculés vers l’e-mail.}}',
  'twoFactor.admin.methods.save': 'Enregistrer les méthodes',
  'twoFactor.admin.methods.cancel': 'Annuler mes modifications',
  'twoFactor.admin.methods.saved': 'Méthodes proposées mises à jour.',
  'twoFactor.admin.methods.superAdminOnly':
    'Seul un super-administrateur peut enregistrer ce réglage. Les autres comptes le consultent.',
  'twoFactor.admin.users.title': 'Qui utilise quoi',
  'twoFactor.admin.users.countEmail': 'comptes en réception par e-mail',
  'twoFactor.admin.users.countApp': 'comptes avec une application',
  'twoFactor.admin.users.appUsage.hasUsers':
    'La méthode par application est proposée et utilisée.',
  'twoFactor.admin.users.appUsage.nobodyYet':
    'La méthode par application est proposée, mais personne ne l’a encore activée.',
  'twoFactor.admin.users.appUsage.appNotOffered':
    'Personne n’utilise l’application parce qu’elle n’est pas proposée : cochez-la ci-dessus pour l’ouvrir aux comptes.',
  'twoFactor.admin.users.appUsage.appUnavailable':
    'Personne n’utilise l’application parce que cette installation ne peut pas conserver les clés en sécurité.',
  'twoFactor.admin.users.search': 'Rechercher',
  'twoFactor.admin.users.searchPlaceholder': 'Nom, adresse ou rôle',
  'twoFactor.admin.users.filterMethod': 'Méthode',
  'twoFactor.admin.users.filterAll': 'Toutes les méthodes',
  'twoFactor.admin.users.caption':
    '{count, plural, one {# compte affiché} other {# comptes affichés}}',
  'twoFactor.admin.users.col.account': 'Compte',
  'twoFactor.admin.users.col.role': 'Rôle',
  'twoFactor.admin.users.col.method': 'Méthode',
  'twoFactor.admin.users.col.codes': 'Codes de secours',
  'twoFactor.admin.users.col.updatedAt': 'Dernière modification',
  'twoFactor.admin.users.col.action': 'Action',
  'twoFactor.admin.users.codesRemaining':
    '{count, plural, one {# code restant} other {# codes restants}}',
  'twoFactor.admin.users.codesNa': 'Sans objet',
  'twoFactor.admin.users.neverUpdated': 'Jamais',
  'twoFactor.admin.users.empty': 'Aucun compte ne correspond à cette recherche.',
  'twoFactor.admin.reset.action': 'Réinitialiser',
  'twoFactor.admin.reset.title': 'Réinitialiser la double authentification ?',
  'twoFactor.admin.reset.body':
    'Vous vous apprêtez à réinitialiser la double authentification de {name} ({email}).',
  'twoFactor.admin.reset.effect1':
    'Ce compte recevra de nouveau son code par e-mail, dès sa prochaine connexion.',
  'twoFactor.admin.reset.effect2':
    'La liaison avec son application est effacée : l’ancien téléphone ne donnera plus aucun code valable.',
  'twoFactor.admin.reset.effect3':
    'Ses codes de secours restants sont invalidés. La personne pourra relier un nouveau téléphone quand elle le souhaitera.',
  'twoFactor.admin.reset.audited':
    'Cette action est enregistrée au journal d’audit : qui l’a faite, sur quel compte, et quand.',
  'twoFactor.admin.reset.cancel': 'Annuler',
  'twoFactor.admin.reset.confirm': 'Réinitialiser ce compte',
  'twoFactor.admin.reset.done':
    'La double authentification de {name} repasse par e-mail.',
  'twoFactor.admin.reset.refused.notAllowed':
    'Réservé aux administrateurs',
  'twoFactor.admin.reset.refused.superAdmin':
    'Seul un super-administrateur peut réinitialiser ce compte',
  'twoFactor.admin.reset.refused.alreadyEmail':
    'Rien à débloquer : déjà par e-mail',
};

// ──────────────────────────────────────────────────────────────────────────
// Notes Mika :
//  - Toutes les clés portent le préfixe `twoFactor.` : c'est ce qui garantit
//    l'absence de collision avec le noyau, et le test `fr.subcatalogs` le
//    vérifie mécaniquement.
//  - Les anciennes clés `totpEnroll.*` ont été retirées de `fr.ts` avec la
//    refonte de l'écran : elles décrivaient un parcours en 3 étapes qui
//    n'existe plus.
// ──────────────────────────────────────────────────────────────────────────
